import React, { useEffect, useState } from 'react';
import { apiErrorText } from '../../utils/apiErrorText';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BookmarkPlus, CalendarPlus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const weekdayOf = (date) => (new Date(`${date}T12:00:00`).getDay() + 6) % 7;

// Monday-start weeks touching the month: [{start: Date, label}]
const weeksOf = (month) => {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1, 12);
  const last = new Date(y, m, 0, 12);
  const d = new Date(first); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const out = [];
  while (d <= last) {
    const s = new Date(d); const e = new Date(d); e.setDate(e.getDate() + 6);
    out.push({ start: s, key: iso(s), label: `${iso(s < first ? first : s).slice(5)} – ${iso(e > last ? last : e).slice(5)}` });
    d.setDate(d.getDate() + 7);
  }
  return out;
};

/** Save the selected trips as a weekly route (weekday + route). */
export const SaveRouteTemplate = ({ rows, selected, disabled, onSaved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const picked = rows.filter((r) => selected.includes(r.id) && r.date);
  const save = async () => {
    try {
      await axios.post(`${API}/mileage-route-templates`, { name, trips: picked.map((r) => ({ weekday: weekdayOf(r.date), purpose: r.purpose || '', origin: r.origin || '', destination: r.destination || '', km: r.km === '' || r.km == null ? null : Number(r.km), driver: r.driver || '' })) });
      toast.success(t('mileage.tpl.saved')); setOpen(false); setName(''); onSaved?.();
    } catch (e) { toast.error(apiErrorText(e, t, 'mileage.failed')); }
  };
  return (
    <>
      <Button size="sm" variant="outline" disabled={disabled || !picked.length} onClick={() => setOpen(true)} data-testid="mileage-save-template"><BookmarkPlus className="h-4 w-4 mr-1" />{t('mileage.tpl.save', { count: picked.length })}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="mileage-save-template-dialog">
          <DialogHeader><DialogTitle>{t('mileage.tpl.saveTitle')}</DialogTitle><DialogDescription>{t('mileage.tpl.saveHint')}</DialogDescription></DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('mileage.tpl.namePh')} data-testid="mileage-template-name" />
          <DialogFooter><Button disabled={!name.trim()} onClick={save} data-testid="mileage-template-save-confirm">{t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Pick a weekly route and the weeks to fill; adds unsigned trips for those weeks inside the month. */
export const AddRouteTemplate = ({ month, onAdd, disabled }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [tid, setTid] = useState('');
  const [weeks, setWeeks] = useState([]);
  const all = weeksOf(month);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/mileage-route-templates`).then((r) => { setList(r.data); setTid((p) => p || r.data[0]?.id || ''); }).catch(() => {});
  }, [open]);
  const tpl = list.find((x) => x.id === tid);
  const add = () => {
    const [y, m] = month.split('-').map(Number);
    const rows = [];
    all.filter((w) => weeks.includes(w.key)).forEach((w) => tpl.trips.forEach((tr) => {
      const d = new Date(w.start); d.setDate(d.getDate() + tr.weekday);
      if (d.getFullYear() === y && d.getMonth() + 1 === m) rows.push({ ...tr, date: iso(d), km: tr.km ?? '', odometer: '' });
    }));
    onAdd(rows);
    toast.success(t('mileage.tpl.added', { count: rows.length }));
    setOpen(false); setWeeks([]);
  };
  const remove = async () => {
    try { await axios.delete(`${API}/mileage-route-templates/${tid}`); setList((l) => l.filter((x) => x.id !== tid)); setTid(''); }
    catch (e) { toast.error(apiErrorText(e, t, 'mileage.failed')); }
  };
  return (
    <>
      <Button size="sm" variant="outline" disabled={disabled} onClick={() => setOpen(true)} data-testid="mileage-add-template"><CalendarPlus className="h-4 w-4 mr-1" />{t('mileage.tpl.add')}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="mileage-add-template-dialog">
          <DialogHeader><DialogTitle>{t('mileage.tpl.addTitle')}</DialogTitle><DialogDescription>{t('mileage.tpl.addHint')}</DialogDescription></DialogHeader>
          {!list.length ? <p className="text-sm text-slate-500" data-testid="mileage-template-none">{t('mileage.tpl.none')}</p> : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Select value={tid || undefined} onValueChange={setTid}>
                  <SelectTrigger className="h-9 flex-1" data-testid="mileage-template-select"><SelectValue placeholder={t('mileage.tpl.pick')} /></SelectTrigger>
                  <SelectContent>{list.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} ({x.trips.length})</SelectItem>)}</SelectContent>
                </Select>
                <Button size="icon" variant="ghost" className="text-red-600" disabled={!tid} onClick={remove} aria-label={t('mileage.tpl.delete')} data-testid="mileage-template-delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
              {tpl && <p className="text-xs text-slate-500">{tpl.trips.map((tr) => `${t(`mileage.tpl.wd.${tr.weekday}`)}: ${tr.origin || '…'} → ${tr.destination || '…'}`).join(' · ')}</p>}
              <div className="grid grid-cols-2 gap-2">
                {all.map((w) => {
                  const [y, mo] = month.split('-').map(Number);
                  const hits = (tpl?.trips || []).some((tr) => { const d = new Date(w.start); d.setDate(d.getDate() + tr.weekday); return d.getFullYear() === y && d.getMonth() + 1 === mo; });
                  return (
                    <label key={w.key} className={`flex items-center gap-2 rounded border px-2 py-1.5 text-sm ${hits ? '' : 'opacity-50'}`} data-testid={`mileage-template-week-${w.key}`}>
                      <Checkbox disabled={!hits} checked={weeks.includes(w.key)} onCheckedChange={(v) => setWeeks((p) => (v ? [...p, w.key] : p.filter((x) => x !== w.key)))} />{w.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter><Button disabled={!tpl || !weeks.length} onClick={add} data-testid="mileage-template-add-confirm">{t('mileage.tpl.addConfirm', { count: weeks.length })}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
